import React, { useState, useContext } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Box,
    Alert,
    Link as MuiLink,
    Select,
    MenuItem,
    FormControl,
    Container,
    Dialog,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { Link } from 'react-router-dom'; // Se agregó useLocation
import logo from '../../assets/logo-viewser.png';
import loginImage from '../../assets/login-image2.webp';
import { useTranslation } from 'react-i18next';
import { LanguageContext } from '../../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const ActualizarContraseña = () => {
    const [openDialog, setOpenDialog] = useState(false);
    const { t } = useTranslation();
    const { changeLanguage, language } = useContext(LanguageContext);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const validationSchema = Yup.object().shape({
        password: Yup.string()
            .min(8, t('validation.passwordLength'))
            .max(20, t('validation.passwordLength'))
            .matches(/[^a-zA-Z0-9]/, t('validation.passwordSpecialChar'))
            .required(t('validation.requiredPassword')),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('password'), null], t('validation.passwordMismatch'))
            .required(t('validation.requiredConfirmPassword')),
    });

    const formik = useFormik({
        initialValues: {
            password: '',
            confirm_password: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setError('');
            setSuccess('');
            const { error } = await supabase.auth.updateUser({
                password: values.password,
            });

            if (error) {
                setError(error.message);
            } else {
                setSuccess('La contraseña ha sido actualizada correctamente');
                setTimeout(() => navigate('/login'), 2000);
            }
            setSubmitting(false);
        },
    });


    // Manejo del cambio de idioma
    const handleLanguageChange = (event) => {
        const lng = event.target.value;
        changeLanguage(lng);
    };


    return (
        <Grid container sx={{ height: '100vh', overflow: 'hidden', mt: { xs: -7, md: 0 } }}>
            {/* Columna Izquierda - Formulario */}
            <Grid
                item
                xs={12}
                md={6}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: { xs: 10, sm: 12 },
                    paddingBottom: { xs: 2, sm: 3 },
                    paddingLeft: { xs: 3, sm: 6 },
                    paddingRight: { xs: 3, sm: 6 },
                    height: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        flexGrow: 1,
                    }}
                >
                    {/* Título */}
                    <Typography
                        variant="h3"
                        sx={{
                            color: '#000000',
                            textShadow: '#7894CC 1px 0 19.5px;',
                            mb: 6,
                            fontWeight: 500,
                            textAlign: 'center',
                            fontSize: { xs: '1.8rem', sm: '2.7rem' },
                            paddingLeft: { xs: 0, md: 8 },
                            paddingRight: { xs: 0, md: 8 },
                        }}
                    >
                        <Box component="span" sx={{ color: '#0F52BD' }}>
                            Viewser
                        </Box>
                        , {t('login.titleSuffix')}
                    </Typography>
                    {/* Contenedor del Formulario */}
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: '500px',
                            backgroundColor: '#FFFFFF',
                            padding: { xs: 2, sm: 3 },
                            borderRadius: '8px',
                            boxShadow: '#A7BBE6 1px 0 50.5px',
                            mt: { xs: -2, md: 0 }
                        }}
                    >

                        <Typography variant='h5' sx={{ color: 'black' }}>Actualización de contraseña</Typography>
                        <Typography variant="body2" sx={{ color: '#888888', mt: 1 }}>
                            Introduce la nueva contraseña que quieres establecer en tu cuenta
                        </Typography>

                        {/* Formulario de Login */}
                        <Box component="form" onSubmit={formik.handleSubmit} mt={1}>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                            <TextField
                                fullWidth
                                label='Nueva contraseña'
                                name="password"
                                type="password"
                                margin="dense"
                                value={formik.values.password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.password && Boolean(formik.errors.password)}
                                helperText={formik.touched.password && formik.errors.password}
                                required
                            />

                            <TextField
                                fullWidth
                                label='Repita su contraseña'
                                name="confirm_password"
                                type="password"
                                margin="dense"
                                value={formik.values.confirm_password}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.confirm_password && Boolean(formik.errors.confirm_password)}
                                helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                                required
                            />

                            {/* Checkboxes de Validación */}
                            <Box sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formik.values.password.length >= 8 && formik.values.password.length <= 20}
                                            disabled
                                            color="primary"
                                            sx={{
                                                '&.Mui-disabled': {
                                                    color: formik.values.password.length >= 8 && formik.values.password.length <= 20 ? '#1976d2' : 'rgba(0, 0, 0, 0.26)',
                                                },
                                            }}
                                        />
                                    }
                                    label={t('register.passwordRequirement1')}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={/[!ºª|@·#$~%¬/()='?¡¿`^+[*{}¨Ç´\-_.:,;]/.test(formik.values.password)}
                                            disabled
                                            color="primary"
                                            sx={{
                                                '&.Mui-disabled': {
                                                    color: /[!ºª|@·#$~%¬/()='?¡¿`^+[*{}¨Ç´\-_.:,;]/.test(formik.values.password) ? '#1976d2' : 'rgba(0, 0, 0, 0.26)',
                                                },
                                            }}
                                        />
                                    }
                                    label={t('register.passwordRequirement2')}
                                />
                            </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={formik.isSubmitting}
                                sx={{
                                    borderRadius: '5px',
                                    textTransform: 'none',
                                    paddingY: 1.2,
                                    mt: 2,
                                }}
                            >
                                Actualizar
                            </Button>
                        </Box>

                    </Box>
                    <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Typography variant="body2" sx={{ color: '#555' }}>
                            ¿Recuerdas tu contraseña? {' '}
                            <MuiLink component={Link} to="/login" sx={{ color: '#0F52BD', fontWeight: 'medium', textDecoration: 'none' }}>
                                {t('register.loginHere')}
                            </MuiLink>
                        </Typography>
                    </Box>
                </Box>
                {/* Logo y Enlaces Inferiores */}
                <Box
                    sx={{
                        bottom: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Box component="img" src={logo} alt="Viewser Logo" sx={{ width: '120px', mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {/* Selector de Idioma */}
                        <FormControl variant="standard" sx={{ minWidth: 80 }}>
                            <Select
                                labelId="language-select-label"
                                id="language-select"
                                value={language}
                                onChange={handleLanguageChange}
                                label={t('login.language')}
                                sx={{
                                    color: '#74767A',
                                    '& .MuiSvgIcon-root': { color: '#74767A' },
                                    fontSize: '14px',
                                    minWidth: '80px',
                                }}
                            >
                                <MenuItem value="es">Español</MenuItem>
                                <MenuItem value="en">English</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Enlace de Ayuda */}
                        <MuiLink href="#" variant="body2" sx={{ color: '#74767A', textDecorationColor: '#74767A' }}>
                            {t('login.help')}
                        </MuiLink>
                        {/* Enlace de Política de Privacidad */}
                        <MuiLink href="#" variant="body2" sx={{ color: '#74767A', textDecorationColor: '#74767A' }}>
                            {t('login.privacyPolicy')}
                        </MuiLink>
                    </Box>
                </Box>
            </Grid>
            {/* Columna Derecha - Imagen */}
            <Grid
                item
                xs={false}
                md={6}
                sx={{
                    display: { xs: 'none', md: 'block' },
                    position: 'relative',
                    height: '100%',
                }}
            >
                <Box
                    component="img"
                    src={loginImage}
                    alt="Login Image"
                    sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.8,
                    }}
                />
            </Grid>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen>
                <Container maxWidth="xs" sx={{ mt: 10, textAlign: 'center', padding: 3 }}>
                    <Alert severity="info">Confirma tu correo electrónico</Alert>
                </Container>
            </Dialog>

        </Grid>
    );
};
export default ActualizarContraseña;